import { useSnackbar } from 'notistack';
import axios, { AxiosResponse } from 'axios';
import { buildUrl } from '../../../utils/urlUtils';
import { useCallback, useEffect, useState } from 'react';
import RestErrorCode from '../../../constants/RestErrorCode';

export interface ApiOptions {
  method: 'get' | 'post' | 'delete';
  baseUrl: string;
  urlPath: string;
  lazy: boolean;
  isFormData?: boolean;
  manualErrorHandling?: boolean;
}

export interface ApiInterface<P, R> {
  execute: (executeParams?: P) => Promise<R>;
  data: R | undefined;
  error: any;
  loading: boolean;
}

declare type ErrorMapping = {
  [key in RestErrorCode]: string;
};

const errorMapping: ErrorMapping = {
  UnexpectedError: 'Unknown Server Error',
  WrongCredentials: 'The email or password you entered is incorrect',
  DuplicateEmail: 'This email address is already in use',
  DuplicatePhone: 'This phone number is already in use',
  AlreadyExists: 'This is a duplicate',
  UserEmailNotFound: 'Email address has not been found on an active account',
  UserActivationFailed: 'The email and activation code do not match',
  UserPasswordResetCodeNotFound:
    'Password reset code not found please request a new one',
  UserPasswordResetCodeExpired:
    'Password reset code has expired please request a new one',
  InvalidFileType: 'File type is not allowed',
  DuplicateEntry: 'This item already exists',
  ScanMarkedAsUnknown:
    'There are still some items for this consultant that need review',
  ApproverMustNotBeReviewer:
    'Approval is only allowed if you did not migrate the case',
  InventoryDeleteFailedInventoryLinked:
    'Delete failed as the inventory is linked to card item/s and a new valid inventory to link to has not been supplied',
  InventoryDeleteFailedSameInventory:
    'The item to link card items to can not be the item that is being deleting',
};

const useApi = <P, R>(
  options: ApiOptions,
  params?: P,
  transformParams?: (p: P) => any
): ApiInterface<P, R> => {
  const [data, setData] = useState<R>();
  const [error, setError] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const url = buildUrl([options.baseUrl, 'api', options.urlPath]);
  const { enqueueSnackbar } = useSnackbar();

  const execute = useCallback(
    (executeParams?: P): Promise<R> => {
      return new Promise<R>((resolve, reject) => {
        setLoading(true);

        let workingParams = executeParams;
        if (transformParams && executeParams) {
          workingParams = transformParams(executeParams);
        }

        const accessToken = localStorage.getItem('access_token');
        const connectionTimeout = 30000;
        const source = axios.CancelToken.source();

        // const processingThreshold = 1500;
        // const processingInterval = 2000;
        // let toastTimer: NodeJS.Timeout | null = null;

        // const startToastLoop = () => {
        //   toastTimer = setInterval(() => {
        //     enqueueSnackbar('Processing...', {
        //       variant: 'warning',
        //     });
        //   }, processingInterval);
        // };

        // const stopToastLoop = () => {
        //   if (toastTimer) {
        //     clearInterval(toastTimer);
        //     toastTimer = null;
        //   }
        // };

        // const warningTimeout = setTimeout(() => {
        //   startToastLoop();
        // }, processingThreshold);

        setTimeout(() => {
          source.cancel('Timeout');
        }, connectionTimeout);

        axios(url, {
          method: options.method,
          headers: {
            Accept: 'application/json',
            'Content-Type': options.isFormData
              ? 'multipart/form-data'
              : 'application/json',
            Authorization: accessToken ? `Bearer ${accessToken}` : '',
          },
          withCredentials: true,
          data:
            options.method === 'post'
              ? workingParams ?? params ?? {}
              : undefined,
          params:
            options.method === 'get' || options.method === 'delete'
              ? workingParams ?? params ?? {}
              : undefined,
          cancelToken: source.token,
        })
          .then(async (response: AxiosResponse<R>) => {
            setData(response.data);
            resolve(response.data);
          })
          .catch((error: any) => {
            if (options.manualErrorHandling) {
              setError(error.response);
              reject(error.response);
            } else {
              const mappedError: string | undefined =
                errorMapping[error.response?.data?.code as RestErrorCode];
              if (mappedError) {
                enqueueSnackbar(mappedError, { variant: 'error' });
              } else {
                enqueueSnackbar(
                  error.response?.status === 403
                    ? 'This request is forbidden'
                    : 'Unknown Server Error',
                  { variant: 'error' }
                );
              }
            }
          })
          .finally(() => {
            // clearTimeout(warningTimeout);
            // stopToastLoop();
            setLoading(false);
          });
      });
    },
    [options, params, url, transformParams, enqueueSnackbar]
  );

  useEffect(() => {
    if (!options.lazy) {
      execute();
    }
  }, [execute, options.lazy]);

  return { execute, data, error, loading };
};

export default useApi;
